import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from '@environment';
import type {
  OfferablePlanDto,
  PlanChangeCancelResultDto,
  PlanChangePreviewDto,
  PlanChangeResultDto,
} from '@sharedlib/dtos/dto/accounts/billing-plan-change.dto';
import type { PaymentReceiptDto } from '@sharedlib/dtos/dto/accounts/billing-quote.dto';
import type { PaymentRetryResultDto } from '@sharedlib/dtos/dto/accounts/billing-payment-retry.dto';
import { tap, type Observable } from 'rxjs';

const BILLING_URL = '/api/account/billing';

@Injectable({ providedIn: 'root' })
export class PlanChangeService {
  private readonly http = inject(HttpClient);

  private readonly changes = signal(0);

  /** Bumped by every mutation, so a resource keyed on it re-reads. */
  public readonly revision = this.changes.asReadonly();

  public listPlans(): Observable<OfferablePlanDto[]> {
    return this.http.get<OfferablePlanDto[]>(`${environment.baseUrl}${BILLING_URL}/plans`);
  }

  /** What the change would cost. No side effects, so it is safe to call as the customer browses. */
  public preview(planUid: string): Observable<PlanChangePreviewDto> {
    return this.http.post<PlanChangePreviewDto>(`${environment.baseUrl}${BILLING_URL}/plan-change/preview`, {
      planUid,
    });
  }

  /**
   * Applies the change.
   *
   * @param expectedAmountCents the figure the customer was shown, which the server re-checks against a
   * fresh quote before charging — so a price that moved refuses rather than bills a different amount.
   */
  public apply(planUid: string, expectedAmountCents: number): Observable<PlanChangeResultDto> {
    return this.http
      .post<PlanChangeResultDto>(`${environment.baseUrl}${BILLING_URL}/plan-change`, { planUid, expectedAmountCents })
      .pipe(tap(() => this.changed()));
  }

  /** Charges the card on file now, for an account whose last payment failed. */
  // Bumps the revision whichever way it went: a declined retry still moves the provider's own dunning
  // clock, and a recovered one changes the status the whole page is drawn from.
  public retryPayment(): Observable<PaymentRetryResultDto> {
    return this.http
      .post<PaymentRetryResultDto>(`${environment.baseUrl}${BILLING_URL}/payment-retry`, {})
      .pipe(tap(() => this.changed()));
  }

  /** The newest settled payment at or after `since` (ISO), or `null`. Calls the provider: never on page load. */
  public latestPayment(since: string): Observable<PaymentReceiptDto | null> {
    return this.http.get<PaymentReceiptDto | null>(`${environment.baseUrl}${BILLING_URL}/payments/latest`, {
      params: new HttpParams().set('since', since),
    });
  }

  public cancelScheduled(): Observable<PlanChangeCancelResultDto> {
    return this.http
      .delete<PlanChangeCancelResultDto>(`${environment.baseUrl}${BILLING_URL}/plan-change`)
      .pipe(tap(() => this.changed()));
  }

  /** Re-reads everything keyed on {@link revision}, for a change this client did not make itself. */
  // Needed by the authentication return: the change was applied while the app was not running.
  public refresh(): void {
    this.changed();
  }

  private changed(): void {
    this.changes.update((revision) => revision + 1);
  }
}
